import React from 'react';
import { Link } from 'react-router-dom';
import {
    AlertTriangle,
    ArrowRight,
    CheckCircle2,
    CircleDot,
    FolderKanban,
    MessageSquare,
    Pencil,
    Plus,
    Trash2,
} from 'lucide-react';
import { useDashboard } from '../hooks/useApi';
import {
    DepartmentWorkloadChart,
    PriorityChart,
    ProjectStatusChart,
    TeamPerformanceChart,
} from '../components/ReportCharts';
import {
    Avatar,
    Card,
    CardBody,
    CardHeader,
    ChartSkeleton,
    EmptyState,
    ErrorState,
    ListSkeleton,
    PageHeader,
    PriorityBadge,
    StatCard,
    StatCardSkeleton,
} from '../components/ui';

const ACTIVITY_META = {
    create: { icon: Plus, tone: 'bg-success-subtle text-success-strong' },
    update: { icon: Pencil, tone: 'bg-info-subtle text-info-strong' },
    delete: { icon: Trash2, tone: 'bg-danger-subtle text-danger-strong' },
    comment: { icon: MessageSquare, tone: 'bg-primary-50 text-primary-600' },
};

const formatDay = (value) =>
    value
        ? new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
        : '—';

const Dashboard = () => {
    const { data: dashboard, isLoading, isError, refetch } = useDashboard();

    const stats = [
        {
            label: 'Total Projects',
            value: dashboard?.project_stats?.total_projects || 0,
            icon: FolderKanban,
            tone: 'primary',
            caption: `${dashboard?.project_stats?.completed_projects ?? 0} completed`,
            to: '/projects',
        },
        {
            label: 'Active Tasks',
            value: dashboard?.task_stats?.in_progress_tasks || 0,
            icon: CircleDot,
            tone: 'info',
            caption: `${dashboard?.task_stats?.total_tasks ?? 0} tasks in total`,
            to: '/tasks',
        },
        {
            label: 'Overdue Tasks',
            value: dashboard?.task_stats?.overdue_tasks || 0,
            icon: AlertTriangle,
            tone: 'danger',
            caption:
                (dashboard?.task_stats?.overdue_tasks || 0) === 0
                    ? 'Nothing past its due date'
                    : 'Needs attention',
            to: '/tasks',
        },
        {
            label: 'Completed Tasks',
            value: dashboard?.task_stats?.completed_tasks || 0,
            icon: CheckCircle2,
            tone: 'success',
            caption: 'Delivered to date',
            to: '/tasks',
        },
    ];

    if (isError) {
        return (
            <>
                <PageHeader title="Dashboard" description="Overview of projects, tasks and team activity." />
                <Card>
                    <ErrorState
                        title="Dashboard data could not be loaded"
                        description="The reporting service did not respond. Retry, or continue to Projects in the meantime."
                        onRetry={refetch}
                    />
                </Card>
            </>
        );
    }

    return (
        <>
            <PageHeader
                title="Dashboard"
                description="Overview of projects, tasks and team activity."
                breadcrumbs={[{ label: 'Home', to: '/' }, { label: 'Dashboard' }]}
            />

            {/* KPIs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                {isLoading
                    ? Array.from({ length: 4 }).map((_, i) => <StatCardSkeleton key={i} />)
                    : stats.map((s) => <StatCard key={s.label} {...s} />)}
            </div>

            {/* Distributions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
                <Card>
                    <CardHeader title="Task Priority" subtitle="Open tasks by priority level" />
                    <CardBody>
                        {isLoading ? (
                            <ChartSkeleton height={200} />
                        ) : (
                            <PriorityChart data={dashboard?.priority_distribution} />
                        )}
                    </CardBody>
                </Card>

                <Card>
                    <CardHeader title="Project Status" subtitle="Portfolio by delivery stage" />
                    <CardBody>
                        {isLoading ? (
                            <ChartSkeleton height={200} />
                        ) : (
                            <ProjectStatusChart data={dashboard?.project_status_distribution} />
                        )}
                    </CardBody>
                </Card>
            </div>

            {/* Performance */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
                <Card className="lg:col-span-2">
                    <CardHeader title="Team Performance" subtitle="Task outcomes per team member" />
                    <CardBody className="h-[300px]">
                        {isLoading ? <ChartSkeleton height={250} /> : (
                            <TeamPerformanceChart data={dashboard?.team_performance} />
                        )}
                    </CardBody>
                </Card>

                <Card>
                    <CardHeader title="Workload by Department" subtitle="Active tasks" />
                    <CardBody className="h-[300px]">
                        {isLoading ? <ChartSkeleton height={250} /> : (
                            <DepartmentWorkloadChart data={dashboard?.department_workload} />
                        )}
                    </CardBody>
                </Card>
            </div>

            {/* Operational lists */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
                <Card className="lg:col-span-2 flex flex-col">
                    <CardHeader
                        title="Upcoming Deadlines"
                        actions={
                            <Link
                                to="/tasks"
                                className="inline-flex items-center gap-1 text-label font-medium text-secondary-600 hover:text-secondary-700 transition-colors duration-150"
                            >
                                All tasks
                                <ArrowRight size={13} aria-hidden="true" />
                            </Link>
                        }
                    />
                    {isLoading ? (
                        <CardBody>
                            <ListSkeleton rows={4} />
                        </CardBody>
                    ) : dashboard?.upcoming_deadlines?.length ? (
                        <ul className="divide-y divide-[var(--divider-color)]">
                            {dashboard.upcoming_deadlines.slice(0, 6).map((task) => (
                                <li key={task.id}>
                                    <Link
                                        to={`/tasks/${task.id}`}
                                        className="flex items-center gap-3 px-5 py-2.5 hover:bg-[var(--bg-subtle)] transition-colors duration-150"
                                    >
                                        <span className="w-11 shrink-0 text-center">
                                            <span className="block text-micro text-ink-500 uppercase tracking-wide">
                                                {task.due_date
                                                    ? new Date(task.due_date).toLocaleDateString(undefined, { month: 'short' })
                                                    : '—'}
                                            </span>
                                            <span className="block text-md font-semibold text-ink-900 tabular leading-tight">
                                                {task.due_date ? new Date(task.due_date).getDate() : '·'}
                                            </span>
                                        </span>

                                        <span className="min-w-0 flex-1">
                                            <span className="block text-base font-medium text-ink-900 truncate">
                                                {task.title}
                                            </span>
                                            <span className="block text-label text-ink-500 truncate">
                                                {task.project_name}
                                            </span>
                                        </span>

                                        <PriorityBadge priority={task.priority} className="shrink-0" />

                                        {task.assignee_name && (
                                            <Avatar
                                                name={task.assignee_name}
                                                size="sm"
                                                className="shrink-0 hidden sm:inline-flex"
                                            />
                                        )}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <EmptyState
                            icon={CheckCircle2}
                            title="No upcoming deadlines"
                            description="Nothing is due in the near term. You're all caught up."
                        />
                    )}
                </Card>

                <Card className="flex flex-col">
                    <CardHeader title="Recent Activity" />
                    <CardBody className="flex-1">
                        {isLoading ? (
                            <ListSkeleton rows={5} />
                        ) : dashboard?.recent_activities?.length ? (
                            <ol className="space-y-3.5">
                                {dashboard.recent_activities.slice(0, 8).map((activity) => {
                                    const meta =
                                        ACTIVITY_META[activity.action] || {
                                            icon: Pencil,
                                            tone: 'bg-ink-100 text-ink-600',
                                        };
                                    const Icon = meta.icon;
                                    return (
                                        <li key={activity.id} className="flex gap-2.5">
                                            <span
                                                aria-hidden="true"
                                                className={`shrink-0 w-6 h-6 rounded-md flex items-center justify-center ${meta.tone}`}
                                            >
                                                <Icon size={12} />
                                            </span>
                                            <span className="min-w-0 flex-1">
                                                <span className="block text-base text-ink-700 leading-snug">
                                                    <span className="font-medium text-ink-900">
                                                        {activity.user_name}
                                                    </span>{' '}
                                                    {activity.description}
                                                </span>
                                                <span className="block text-micro text-ink-400 mt-0.5 tabular">
                                                    {formatDay(activity.created_at)} ·{' '}
                                                    {new Date(activity.created_at).toLocaleTimeString(undefined, {
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                    })}
                                                </span>
                                            </span>
                                        </li>
                                    );
                                })}
                            </ol>
                        ) : (
                            <EmptyState
                                compact
                                title="No activity yet"
                                description="Changes made across projects and tasks appear here."
                            />
                        )}
                    </CardBody>
                </Card>
            </div>
        </>
    );
};

export default Dashboard;
